// dev/app-skeleton/src/lib/backup.ts
// L3 加密导出 / 导入（.diary.enc）。
// 关键决策（PRD v1.1）：
//   1) backup_key = HKDF(root_key, "backup|v1|" + backup_id) —— 必须已登录解锁 root_key 才能用。
//   2) 文件无密码字段、无 wrapped-key —— 任何一台已登录到该账号的设备都能恢复；
//      离线纯密码恢复请走 L1（云端 wrapped key）+ L2（服务器 op-log）。
//   3) AAD 强绑 user_id + backup_id + ts_ms + crypto_version。
//   4) 文件布局是定长 magic + 头长前缀的二进制，便于 SEC 二次校验和未来改 v2。
//
// 文件布局（小端不友好；统一 u32BE）：
//   ┌──────────────────────────────────────────────────────────────┐
//   │  0..7   magic   = "DIARYENC" (ASCII, 8B)                     │
//   │  8      version = 1 (u8)                                     │
//   │  9..11  reserved = 0x00 0x00 0x00                            │
//   │ 12..15  header_len (u32BE)                                   │
//   │ 16..16+H  header_json (UTF-8 JSON):                          │
//   │            { backup_id, crypto_version, user_id,             │
//   │              created_at, entry_count, has_drafts }           │
//   │ +0..3   blob_len (u32BE)                                     │
//   │ +4..    encrypted_blob = [nonce|ct|tag]                      │
//   └──────────────────────────────────────────────────────────────┘
//
// 解密后明文为：
//   { entries: ExportedEntry[], drafts?: ExportedDraft[] }
//
// 注意：媒体本身不打进 L3（体积太大，由 L2/R2 端到端拉回），
//       只携带 attached_media_ids 索引；导入端按需重新拉取。
import { CRYPTO_VERSION, aeadOpen, aeadSeal, buildAAD, zeroize, bytesToBase64, base64ToBytes, } from "../crypto";
import { db } from "./db";
import { deriveFromRoot } from "./session-key";
import { clearEntryCache } from "./entry-cache";
const MAGIC = new TextEncoder().encode("DIARYENC"); // 8B
const FORMAT_VERSION = 1;
// ---------- 工具 ----------
function u32BE(n) {
    const b = new Uint8Array(4);
    new DataView(b.buffer).setUint32(0, n >>> 0, false);
    return b;
}
function readU32BE(b, offset) {
    return new DataView(b.buffer, b.byteOffset + offset, 4).getUint32(0, false);
}
function concat(...parts) {
    const total = parts.reduce((s, p) => s + p.byteLength, 0);
    const out = new Uint8Array(total);
    let off = 0;
    for (const p of parts) {
        out.set(p, off);
        off += p.byteLength;
    }
    return out;
}
// ---------- 导出 ----------
/**
 * 导出 L3 备份。
 * @param userId 当前登录用户 UUID（用于 AAD 绑定）
 * @param opts.includeDrafts 是否把本地未提交草稿一并打包（默认 false：草稿是设备状态）
 */
export async function exportBackup(userId, opts = {}) {
    const backup_id = crypto.randomUUID();
    const created_at = Date.now();
    // 1) 拉本地全量 entries（含已删除墓碑：恢复端可决定是否覆盖）
    const allEntries = await db.entries.toArray();
    const exportedEntries = allEntries.map((e) => ({
        id: e.id,
        encrypted_b64: bytesToBase64(e.encrypted_blob),
        local_known_server_version: e.local_known_server_version,
        deleted: e.deleted,
        created_at: e.created_at,
        updated_at: e.updated_at,
    }));
    // 2) 可选：草稿
    let exportedDrafts;
    if (opts.includeDrafts) {
        const allDrafts = await db.drafts.toArray();
        exportedDrafts = allDrafts.map((d) => ({
            entry_id: d.entry_id,
            encrypted_b64: bytesToBase64(d.encrypted_blob),
            updated_at: d.updated_at,
        }));
    }
    const pt = exportedDrafts
        ? { entries: exportedEntries, drafts: exportedDrafts }
        : { entries: exportedEntries };
    // 3) AEAD seal with backup_key
    const key = deriveFromRoot("backup", backup_id);
    let sealed;
    try {
        const aad = buildAAD({
            kind: "backup",
            user_id: userId,
            resource_id: backup_id,
            ts_ms: created_at,
        });
        const ptBytes = new TextEncoder().encode(JSON.stringify(pt));
        const { bytes } = aeadSeal(key, ptBytes, aad);
        sealed = bytes;
    }
    finally {
        zeroize(key);
    }
    // 4) header
    const header = {
        backup_id,
        crypto_version: CRYPTO_VERSION,
        user_id: userId,
        created_at,
        entry_count: exportedEntries.length,
        has_drafts: !!exportedDrafts?.length,
    };
    const headerBytes = new TextEncoder().encode(JSON.stringify(header));
    // 5) wire
    const reserved = new Uint8Array([0, 0, 0]);
    const file = concat(MAGIC, new Uint8Array([FORMAT_VERSION]), reserved, u32BE(headerBytes.byteLength), headerBytes, u32BE(sealed.byteLength), sealed);
    // TS 5.7+ lib.dom 把 BlobPart 收紧到 BufferSource<ArrayBuffer>；
    // 用 ArrayBuffer 切片显式喂入，避免 Uint8Array<ArrayBufferLike> 不可赋值的报错，
    // 同时不引入隐式 copy（slice 仅复制视图范围）。
    const ab = file.buffer.slice(file.byteOffset, file.byteOffset + file.byteLength);
    return new Blob([ab], { type: "application/octet-stream" });
}
export async function parseBackupFile(file) {
    const buf = new Uint8Array(await file.arrayBuffer());
    if (buf.byteLength < 16)
        throw new Error("backup file too small");
    // magic
    for (let i = 0; i < 8; i++) {
        if (buf[i] !== MAGIC[i])
            throw new Error("not a .diary.enc file (bad magic)");
    }
    const version = buf[8];
    if (version !== FORMAT_VERSION) {
        throw new Error(`unsupported backup format version: ${version}`);
    }
    const headerLen = readU32BE(buf, 12);
    if (16 + headerLen + 4 > buf.byteLength)
        throw new Error("backup truncated (header)");
    const headerJson = new TextDecoder().decode(buf.subarray(16, 16 + headerLen));
    const header = JSON.parse(headerJson);
    const blobLenOffset = 16 + headerLen;
    const blobLen = readU32BE(buf, blobLenOffset);
    const blobStart = blobLenOffset + 4;
    if (blobStart + blobLen !== buf.byteLength)
        throw new Error("backup truncated (payload)");
    const ciphertext = buf.subarray(blobStart, blobStart + blobLen);
    // 基本健壮性
    if (typeof header.backup_id !== "string" || header.backup_id.length !== 36) {
        throw new Error("invalid backup header.backup_id");
    }
    if (header.crypto_version !== CRYPTO_VERSION) {
        throw new Error(`backup crypto_version=${header.crypto_version} not supported by client v${CRYPTO_VERSION}`);
    }
    return { header, ciphertext };
}
/**
 * 导入备份并合并到本地。
 * 合并规则（保留两版，不直接覆盖）：
 *   - 本地无该 id  → 直接落库（applied）。
 *   - 本地有同 id：
 *       a) 本地 dirty=true  → 视为冲突：把"本地脏版本"另存为新 UUID，
 *          再用备份版覆盖原 id；UI 弹合并卡片。
 *       b) 本地 dirty=false 且 备份的 updated_at >= 本地 updated_at → 覆盖（applied）。
 *       c) 否则 → skip。
 *   - 草稿：按 entry_id 直接 put（草稿本就是设备态，最近写入优先）。
 *
 * 调用方必须已经 setRootKey()；user_id 必须等于 header.user_id，否则 AAD 失败。
 */
export async function importBackup(userId, parsed) {
    if (parsed.header.user_id !== userId) {
        throw new Error("backup belongs to a different user_id");
    }
    // 解密 payload
    const key = deriveFromRoot("backup", parsed.header.backup_id);
    let pt;
    try {
        const aad = buildAAD({
            kind: "backup",
            user_id: userId,
            resource_id: parsed.header.backup_id,
            ts_ms: parsed.header.created_at,
        });
        const ptBytes = aeadOpen(key, parsed.ciphertext, aad);
        pt = JSON.parse(new TextDecoder().decode(ptBytes));
    }
    finally {
        zeroize(key);
    }
    const summary = {
        applied: 0,
        skipped: 0,
        conflicts: [],
        drafts_imported: 0,
    };
    await db.transaction("rw", db.entries, db.drafts, async () => {
        for (const e of pt.entries) {
            const local = await db.entries.get(e.id);
            const incoming = {
                id: e.id,
                encrypted_blob: base64ToBytes(e.encrypted_b64),
                local_known_server_version: e.local_known_server_version,
                dirty: false,
                deleted: e.deleted,
                created_at: e.created_at,
                updated_at: e.updated_at,
            };
            if (!local) {
                await db.entries.put(incoming);
                summary.applied++;
                continue;
            }
            if (local.dirty) {
                // 本地脏版另存（SEC F-REV-01 修复 · 修法 A：克隆时重新加密）
                //   旧 bug：{ ...local, id: cloneId } 改了主键，但 encrypted_blob 仍是用
                //   原 entry_id 的 AAD/HKDF entry_key 封的；之后再 export/同步用 cloneId
                //   去派 entry_key + AAD 永远 open 失败 → 该克隆条目"永久不可解密"。
                //   修法：拿原 key 解开 → 用 cloneId 的 entry_key 重新 seal，版本归 1（新 id 新生命周期）。
                const cloneId = crypto.randomUUID();
                const cloneVersion = 1;
                const oldKey = deriveFromRoot("entry", local.id);
                const newKey = deriveFromRoot("entry", cloneId);
                let resealed;
                try {
                    // 用本地已知的 server_version 去开（local 还未推服，base_version 是 local.local_known_server_version；
                    // 但 AAD 里的 version 是"被封时"的 version，本机封时用的就是 local_known_server_version+1）
                    const openVer = (local.local_known_server_version ?? 0) + 1;
                    const openAAD = buildAAD({
                        kind: "entry",
                        user_id: userId,
                        resource_id: local.id,
                        version: openVer,
                    });
                    const ptBytes = aeadOpen(oldKey, local.encrypted_blob, openAAD);
                    const sealAAD = buildAAD({
                        kind: "entry",
                        user_id: userId,
                        resource_id: cloneId,
                        version: cloneVersion,
                    });
                    resealed = aeadSeal(newKey, ptBytes, sealAAD).bytes;
                }
                finally {
                    zeroize(oldKey);
                    zeroize(newKey);
                }
                await db.entries.put({
                    ...local,
                    id: cloneId,
                    encrypted_blob: resealed,
                    local_known_server_version: null, // 新条目，服务端尚未见过
                    dirty: true, // 等同步队列推上去
                    updated_at: Date.now(),
                });
                await db.entries.put(incoming);
                summary.conflicts.push({
                    local_id: e.id,
                    cloned_to_id: cloneId,
                    reason: "local_dirty",
                });
                summary.applied++;
            }
            else if (e.updated_at >= local.updated_at) {
                await db.entries.put(incoming);
                summary.applied++;
            }
            else {
                summary.skipped++;
            }
        }
        if (pt.drafts?.length) {
            for (const d of pt.drafts) {
                await db.drafts.put({
                    entry_id: d.entry_id,
                    encrypted_blob: base64ToBytes(d.encrypted_b64),
                    updated_at: d.updated_at,
                });
                summary.drafts_imported++;
            }
        }
    });
    // D14-DEV-v2：entries 行被批量替换/克隆，version key 与缓存项可能不再一致 → 整清
    clearEntryCache();
    return summary;
}
/** 触发浏览器下载，默认文件名 diary-YYYYMMDD-HHmm.diary.enc */
export function suggestBackupFilename(d = new Date()) {
    const pad = (n) => n.toString().padStart(2, "0");
    return `diary-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}.diary.enc`;
}
// ============================================================================
// PRD v1.1 决策 ⑥：L3「替换全部」前的 24h 加密快照 + 一键撤销
//   - 快照仅本地 IDB（db.revert_snapshots），永不上服务端
//   - 加密 key = HKDF(root_key, "backup|v1|" + snapshot_id)
//   - AAD 强绑 user_id + snapshot_id + ts_ms
//   - TTL = 24h；超时调用 purgeExpiredSnapshots() 物理清除
// ============================================================================
export const REVERT_SNAPSHOT_TTL_MS = 24 * 60 * 60 * 1000;
/** 把当前整库快照成一份加密 row 放进 db.revert_snapshots。返回 snapshot_id。 */
async function snapshotCurrentDb(userId, sourceBackupId) {
    const snapshot_id = crypto.randomUUID();
    const created_at = Date.now();
    const expires_at = created_at + REVERT_SNAPSHOT_TTL_MS;
    const [allEntries, allDrafts] = await Promise.all([
        db.entries.toArray(),
        db.drafts.toArray(),
    ]);
    const pt = {
        entries: allEntries.map((e) => ({
            id: e.id,
            encrypted_b64: bytesToBase64(e.encrypted_blob),
            local_known_server_version: e.local_known_server_version,
            deleted: e.deleted,
            created_at: e.created_at,
            updated_at: e.updated_at,
        })),
        drafts: allDrafts.map((d) => ({
            entry_id: d.entry_id,
            encrypted_b64: bytesToBase64(d.encrypted_blob),
            updated_at: d.updated_at,
        })),
    };
    const key = deriveFromRoot("backup", snapshot_id);
    let sealed;
    try {
        const aad = buildAAD({
            kind: "backup",
            user_id: userId,
            resource_id: snapshot_id,
            ts_ms: created_at,
        });
        const ptBytes = new TextEncoder().encode(JSON.stringify(pt));
        const { bytes } = aeadSeal(key, ptBytes, aad);
        sealed = bytes;
    }
    finally {
        zeroize(key);
    }
    await db.revert_snapshots.put({
        snapshot_id,
        user_id: userId,
        encrypted_blob: sealed,
        entry_count: pt.entries.length,
        draft_count: pt.drafts.length,
        created_at,
        expires_at,
        source_backup_id: sourceBackupId,
    });
    return {
        snapshot_id,
        created_at,
        expires_at,
        entry_count: pt.entries.length,
        draft_count: pt.drafts.length,
        source_backup_id: sourceBackupId,
        remaining_ms: expires_at - Date.now(),
    };
}
/**
 * L3「替换全部」导入：
 *   1) 先把当前整库做加密快照，落 db.revert_snapshots，TTL 24h
 *   2) 清空 entries + drafts
 *   3) 把 backup 内 entries + drafts 全部写入
 *   4) 返回 revert_snapshot 元数据，UI 用它显示倒计时和"撤销"按钮
 *
 * 注意：不会动 ops 表（同步队列由 sync-client 自己消化；
 *       替换后已 applied 的本地态会通过 server pull 重新同步）。
 */
export async function importBackupReplaceAll(userId, parsed) {
    if (parsed.header.user_id !== userId) {
        throw new Error("backup belongs to a different user_id");
    }
    // 先解密 payload —— 失败就别快照、别清库
    const key = deriveFromRoot("backup", parsed.header.backup_id);
    let pt;
    try {
        const aad = buildAAD({
            kind: "backup",
            user_id: userId,
            resource_id: parsed.header.backup_id,
            ts_ms: parsed.header.created_at,
        });
        pt = JSON.parse(new TextDecoder().decode(aeadOpen(key, parsed.ciphertext, aad)));
    }
    finally {
        zeroize(key);
    }
    // 1) 快照（独立事务，保证哪怕替换中途崩溃，撤销点也已存盘）
    const snapMeta = await snapshotCurrentDb(userId, parsed.header.backup_id);
    // 2 + 3) 清库 + 写入（同一事务保证原子）
    let imported_drafts = 0;
    await db.transaction("rw", db.entries, db.drafts, async () => {
        await db.entries.clear();
        await db.drafts.clear();
        for (const e of pt.entries) {
            await db.entries.put({
                id: e.id,
                encrypted_blob: base64ToBytes(e.encrypted_b64),
                local_known_server_version: e.local_known_server_version,
                dirty: false,
                deleted: e.deleted,
                created_at: e.created_at,
                updated_at: e.updated_at,
            });
        }
        if (pt.drafts?.length) {
            for (const d of pt.drafts) {
                await db.drafts.put({
                    entry_id: d.entry_id,
                    encrypted_blob: base64ToBytes(d.encrypted_b64),
                    updated_at: d.updated_at,
                });
                imported_drafts++;
            }
        }
    });
    // entries 整库被替换 → 内存缓存与 row.version 已不一致，清掉
    clearEntryCache();
    return {
        imported_entries: pt.entries.length,
        imported_drafts,
        revert_snapshot: snapMeta,
    };
}
/** UI 列出未过期的快照（按 created_at 倒序）。 */
export async function listRevertSnapshots(userId) {
    const now = Date.now();
    const rows = await db.revert_snapshots
        .where("user_id").equals(userId)
        .toArray();
    return rows
        .filter((r) => r.expires_at > now)
        .sort((a, b) => b.created_at - a.created_at)
        .map((r) => ({
        snapshot_id: r.snapshot_id,
        created_at: r.created_at,
        expires_at: r.expires_at,
        entry_count: r.entry_count,
        draft_count: r.draft_count,
        source_backup_id: r.source_backup_id,
        remaining_ms: r.expires_at - now,
    }));
}
/**
 * 一键撤销：用快照覆盖当前 entries + drafts。
 * 撤销成功后立刻删除该快照（防止重复撤销 / 状态错乱）。
 */
export async function revertReplaceAll(userId, snapshotId) {
    const row = await db.revert_snapshots.get(snapshotId);
    if (!row)
        throw new Error("revert snapshot not found");
    if (row.user_id !== userId)
        throw new Error("snapshot belongs to a different user_id");
    if (row.expires_at <= Date.now()) {
        await db.revert_snapshots.delete(snapshotId);
        throw new Error("revert snapshot expired (>24h)");
    }
    // 解密
    const key = deriveFromRoot("backup", snapshotId);
    let pt;
    try {
        const aad = buildAAD({
            kind: "backup",
            user_id: userId,
            resource_id: snapshotId,
            ts_ms: row.created_at,
        });
        pt = JSON.parse(new TextDecoder().decode(aeadOpen(key, row.encrypted_blob, aad)));
    }
    finally {
        zeroize(key);
    }
    let restored_drafts = 0;
    await db.transaction("rw", db.entries, db.drafts, db.revert_snapshots, async () => {
        await db.entries.clear();
        await db.drafts.clear();
        for (const e of pt.entries) {
            await db.entries.put({
                id: e.id,
                encrypted_blob: base64ToBytes(e.encrypted_b64),
                local_known_server_version: e.local_known_server_version,
                dirty: false,
                deleted: e.deleted,
                created_at: e.created_at,
                updated_at: e.updated_at,
            });
        }
        for (const d of pt.drafts) {
            await db.drafts.put({
                entry_id: d.entry_id,
                encrypted_blob: base64ToBytes(d.encrypted_b64),
                updated_at: d.updated_at,
            });
            restored_drafts++;
        }
        await db.revert_snapshots.delete(snapshotId);
    });
    // entries 整库被快照回放 → 清缓存
    clearEntryCache();
    return { restored_entries: pt.entries.length, restored_drafts };
}
/** 后台周期任务（App 启动 + 每小时）：清掉所有过期快照。返回被清除的条数。 */
export async function purgeExpiredSnapshots() {
    const now = Date.now();
    const expired = await db.revert_snapshots.where("expires_at").belowOrEqual(now).toArray();
    if (!expired.length)
        return 0;
    await db.revert_snapshots.bulkDelete(expired.map((r) => r.snapshot_id));
    return expired.length;
}
